import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { catchError, Subject, takeUntil } from 'rxjs'
import { WatchListResponseInterface } from '@modules/watch-list-module/interfaces/watch-list-response.interface'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { WatchListColumnsEnum } from '@shared/components/trading/widgets/watch-list-widget/core/enums/watch-list-columns.enum'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { TranslateService } from '@ngx-translate/core'
import { HttpErrorResponse } from '@angular/common/http'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { WatchListSettingsInterface } from '@shared/components/trading/widgets/watch-list-widget/core/interfaces/watch-list-settings.interface'
import { WatchListTableComponent } from '@shared/components/trading/watch-list/common/watch-list-table/watch-list-table.component'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { WatchListHelperService } from '@modules/watch-list-module/services/watch-list-helper.service'
import { WatchListAssetInterface } from '@modules/watch-list-module/interfaces/watch-list-asset.interface'
import { AlertService } from '@shared/services/alert.service'
import { SocketStockEvEnum } from '@modules/prices-module/socket-prices/enums/socket-stock-ev.enum'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { SocketStockTInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-t.interface'
import { SocketPricesStocksService } from '@modules/stocks-module/socket-prices/services/socket-prices-stocks.service'
import { SocketPricesHelperService } from '@modules/prices-module/socket-prices/services/socket-prices-helper.service'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { MarketStatusInterface } from '@modules/alpaca-module/market-status/interfaces/market-status.interface'

@UseLocales({ ru, en })
@Component({
	selector: 'app-watch-list',
	templateUrl: './watch-list.component.html',
	styleUrl: './watch-list.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [WatchListTableComponent],
})
export class WatchListComponent implements OnInit, OnDestroy {
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly socketStockEvEnum: typeof SocketStockEvEnum = SocketStockEvEnum

	public widget: InputSignal<WidgetInterface<WatchListSettingsInterface> | null> =
		input<WidgetInterface<WatchListSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _watchListHelperService: WatchListHelperService =
		inject<WatchListHelperService>(WatchListHelperService)
	private readonly _socketPricesStocksService: SocketPricesStocksService =
		inject<SocketPricesStocksService>(SocketPricesStocksService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _socketPricesHelperService: SocketPricesHelperService =
		inject<SocketPricesHelperService>(SocketPricesHelperService)

	protected liveTicker: WritableSignal<LiveTickerInterface | null> = signal<LiveTickerInterface | null>(null)
	protected marketStatus: WritableSignal<MarketStatusInterface | null> = signal<MarketStatusInterface | null>(null)
	protected watchList: WritableSignal<WatchListResponseInterface | null> = signal<WatchListResponseInterface | null>(
		null,
	)
	protected isLoadingWatchList: WritableSignal<boolean> = signal<boolean>(false)
	protected statesColumns: Signal<ColumnInterface<WatchListColumnsEnum>[]> = computed<
		ColumnInterface<WatchListColumnsEnum>[]
	>((): ColumnInterface<WatchListColumnsEnum>[] => [...(this.widget()?.settings.columns ?? [])])

	get tickers(): string[] {
		return this.watchList()?.assets?.map((asset: WatchListAssetInterface): string => asset.symbol) ?? []
	}
	private _listenLiveTicker(): void {
		this._liveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((liveTicker: LiveTickerInterface | null): void => {
				this.liveTicker.set(liveTicker)
				this._updateAssetByLiveTicker()
			})
	}

	private _listenMarketStatus(): void {
		this._marketStatusHelperService.marketStatus$
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: MarketStatusInterface | null): void => {
				this.marketStatus.set(response)
			})
	}

	private _listenResubscribeStocks(): void {
		this._socketPricesStocksService.resubscribeStocks$.pipe(takeUntil(this._destroy$)).subscribe((): void => {
			this._socketPricesStocksService.toggleSubTickers({
				tickers: this.tickers,
				action: 'subscribe',
				entity: this.constructor.name,
			})
		})
	}

	private _launchWatchList(): void {
		this._tradingLauncherService
			.launchWatchList$()
			.pipe(
				takeUntil(this._destroy$),
				catchError((error: HttpErrorResponse): never => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('watchList.actions.getFail'),
					)
					throw error
				}),
			)
			.subscribe(([watchList, loading]: [WatchListResponseInterface | null, boolean]): void => {
				const prevWatchList: WatchListResponseInterface | null = this.watchList()
				this.watchList.set(watchList)
				this.isLoadingWatchList.set(loading)
				this._compareWatchLists(prevWatchList, watchList)
			})
	}

	private _compareWatchLists(
		oldWatchList: WatchListResponseInterface | null,
		newWatchList: WatchListResponseInterface | null,
	): void {
		const oldTickers: string[] =
			oldWatchList?.assets?.map((asset: WatchListAssetInterface): string => asset.symbol) ?? []
		const newTickers: string[] =
			newWatchList?.assets?.map((asset: WatchListAssetInterface): string => asset.symbol) ?? []

		const addedTickers: string[] = newTickers.filter((ticker: string): boolean => !oldTickers.includes(ticker))
		const deletedTickers: string[] = oldTickers.filter((ticker: string): boolean => !newTickers.includes(ticker))

		if (addedTickers.length || deletedTickers.length) {
			this._openSocketStocks()
		}

		this._socketPricesStocksService.toggleSubTickers({
			tickers: deletedTickers,
			action: 'unsubscribe',
			entity: this.constructor.name,
		})

		this._socketPricesStocksService.toggleSubTickers({
			tickers: addedTickers,
			action: 'subscribe',
			entity: this.constructor.name,
		})
	}

	private _openSocketStocks(): void {
		this._socketPricesStocksService
			.openSocketStocks()
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: SocketStocksType[]): void => {
				const watchListAssets: WatchListAssetInterface[] = this.watchList()?.assets ?? []
				const liveTicker: LiveTickerInterface | null = this.liveTicker()

				watchListAssets.forEach((asset: WatchListAssetInterface): void => {
					const ticker: string = asset.symbol
					if (liveTicker?.ticker === ticker) return

					const stockT: SocketStockTInterface | undefined = this._socketPricesHelperService.getStockByEV(
						this.socketStockEvEnum.T,
						response,
						ticker,
					) as SocketStockTInterface | undefined

					if (!stockT) return

					const priorityPrice: number = stockT.p ?? asset.close
					this._watchListHelperService.updateAssetsPrices(ticker, priorityPrice, liveTicker)
				})
			})
	}

	private _updateAssetByLiveTicker(): void {
		const watchList: WatchListResponseInterface | null = this.watchList()
		const liveTicker: LiveTickerInterface | null = this.liveTicker()
		const marketStatus: MarketStatusInterface | null = this.marketStatus()
		if (!watchList || !liveTicker || !marketStatus) return

		const foundAsset: WatchListAssetInterface | undefined = watchList.assets.find(
			(asset: WatchListAssetInterface): boolean => asset.symbol === liveTicker.ticker,
		)
		if (!foundAsset) return

		let priorityPrice: number = 0

		if (marketStatus.status === this.marketStatusEnum.marketOpen) {
			priorityPrice = liveTicker.currentPrices?.price ?? foundAsset.close
		} else {
			priorityPrice = liveTicker.marketStatusPrices?.price ?? foundAsset?.last_trade_price ?? foundAsset.close
		}

		this._watchListHelperService.updateAssetsPrices(foundAsset.symbol, priorityPrice, liveTicker)
	}

	ngOnInit(): void {
		this._listenMarketStatus()
		this._listenLiveTicker()
		this._listenResubscribeStocks()
		this._launchWatchList()
	}

	ngOnDestroy(): void {
		this._socketPricesStocksService.toggleSubTickers({
			tickers: this.tickers,
			action: 'unsubscribe',
			entity: this.constructor.name,
		})
		this._destroy$.next()
		this._destroy$.complete()
	}
}
